/**
 * Every third-party URL that IS SendVC — the single source of truth.
 *
 * WHY THIS FILE EXISTS
 *
 * These URLs used to live in two places: the `sameAs` array in
 * `lib/seo-config.ts` and a separate hard-coded list in the footer component.
 * Two lists mean drift, and drift here is not cosmetic. This project has been
 * bitten by exactly that failure mode repeatedly:
 *
 *   - A Product Hunt URL sat in `sameAs` for months while 404ing.
 *   - A tracker checkbox left unticked after the work was done was later cited
 *     as evidence a live Crunchbase profile had never been created, and nearly
 *     caused it to be deleted from `sameAs`.
 *   - Three live profiles (G2, F6S, StartupRanking) were omitted from the site
 *     for days because a Cloudflare block had been recorded as "absent".
 *
 * So there is now one array. `sameAs` is derived from it, the footer row is
 * derived from it, and they cannot disagree.
 *
 * THE VERIFICATION RULE — do not weaken it
 *
 * Never add a URL by guessing or pattern-matching a handle. Load it first, and
 * record in `verified` what you actually saw. A `sameAs` pointing at a
 * nonexistent or someone else's profile is strictly WORSE than omitting it: it
 * breaks entity resolution instead of helping it. Two live traps this list is
 * built to avoid:
 *
 *   - `crunchbase.com/organization/sendcv` is SendCV, a DIFFERENT company. The
 *     slug differs from ours by one transposed character and it outranks us for
 *     our own name. Check Crunchbase slugs character by character.
 *   - `x.com/sendvc` is an unrelated third party. Ours is `@SendVCapp`.
 *
 * And a blocked fetch is NOT evidence of absence. G2 returns HTTP 403 to every
 * automated client including for pages that certainly exist (Slack's), so a
 * status code from such a host carries zero information. Resolve by rendering
 * the page; if you cannot, record it as unverified rather than letting it
 * quietly become a negative.
 */

export interface SendVCProfile {
  href: string;
  /** Human-facing label. Only used where `footer` is true. */
  label: string;
  /**
   * Whether the target page is served WITHOUT a noindex directive.
   *
   * This is checked per URL, not per domain — G2 gets opposite answers on two
   * of its own paths. It is recorded because it is the honest measure of how
   * much a given entry can contribute: Google still CRAWLS a noindex page and
   * can use it for entity association, so these are kept in `sameAs`, but an
   * indexable page is a materially stronger corroboration than one Google is
   * told not to index.
   */
  indexable: boolean;
  /** What was actually observed, and when. */
  verified: string;
}

export const SENDVC_PROFILES: SendVCProfile[] = [
  {
    href: 'https://www.linkedin.com/company/sendvc',
    label: 'LinkedIn',
    indexable: true,
    verified: '2026-08-26: live company page, confirmed by the site owner.',
  },
  {
    href: 'https://x.com/SendVCapp',
    label: 'X',
    indexable: true,
    verified:
      '2026-08-25: loaded the live profile — bio reads "SendVC is an AI-powered investor outreach platform for founders who are raising" and links sendvc.app. NOTE the handle: @sendvc is a different, unrelated account.',
  },
  {
    href: 'https://www.youtube.com/@SendVC',
    label: 'YouTube',
    indexable: true,
    verified:
      '2026-08-26: confirmed via the demo video\'s own oembed record, which returns author_url = youtube.com/@SendVC. This is the CHANNEL — never point at a video id, they get deleted.',
  },
  {
    href: 'https://www.crunchbase.com/organization/sendvc',
    label: 'Crunchbase',
    indexable: true,
    verified:
      '2026-08-24: loaded the live page — renders Legal Name "Future Webservice", Also Known As "Send VC", Company Type "For Profit", info@sendvc.app, HQ Hoyerswerda. Not re-checkable by tooling since: Crunchbase serves an interactive Cloudflare challenge.',
  },
  {
    href: 'https://www.trustpilot.com/review/sendvc.app',
    label: 'Trustpilot',
    // FLIPPED 2026-10-15: was indexable in August, now serves `noindex`.
    // Still 0 reviews. Same gating pattern G2 applies to zero-review profiles.
    // It should flip back once a real review lands.
    indexable: false,
    verified:
      '2026-08-26: public page renders "Claimed profile", category Software Company, and the same Hoyerswerda address /imprint asserts. Zero reviews, which is fine — sameAs establishes IDENTITY, not reputation, and no rating is carried into our markup.',
  },
  {
    href: 'https://www.g2.com/sellers/sendvc',
    label: 'G2',
    indexable: true,
    verified:
      '2026-08-26: rendered in a real browser (every automated fetch gets a DataDome interstitial). No robots meta at all, self-referencing canonical, names Future Webservice / Hoyerswerda / sendvc.app. This is the SELLER page — the product page at /products/sendvc/reviews is served follow,noindex and is deliberately not listed.',
  },
  {
    href: 'https://www.indiehackers.com/product/sendvc/',
    label: 'Indie Hackers',
    indexable: true,
    verified:
      '2026-08-26: live public product page. A real community profile rather than an auto-generated directory stub.',
  },
  {
    href: 'https://www.startupranking.com/startup/sendvc',
    label: 'StartupRanking',
    indexable: true,
    verified:
      '2026-08-26: rendered while LOGGED OUT, so this is the public view and not an owner preview. Carries our tagline, sendvc.app and the Dec 2025 founding date. No robots meta. Its listing still shows "pending approval" and its region is wrong (Nordrhein-Westfalen; we are in Sachsen).',
  },
  {
    href: 'https://sourceforge.net/software/product/SendVC/',
    label: 'SourceForge',
    indexable: true,
    verified:
      '2026-08-30: 200, title "SendVC Reviews in 2026", contains sendvc.app, no robots meta, self-referencing canonical. Control: /software/product/ZzqNotReal9931/ returns 404 (14 bytes). The tracker had this as "awaiting owner\'s submit click" — that row was stale; it is published.',
  },
  {
    href: 'https://fridayhunt.com/projects/sendvc',
    label: 'FridayHunt',
    indexable: true,
    verified:
      '2026-08-30: 200, 136KB, title "SendVC | FridayHunt", contains sendvc.app, <meta name="robots" content="index, follow">, self-referencing canonical. Control: /projects/zzq-not-real-9931 also returns 200 but only 76KB with zero "sendvc" hits — this host soft-404s, so compare BODIES, not status codes.',
  },
  {
    href: 'https://stackshare.io/sendvc',
    label: 'StackShare',
    // `index, follow`, BUT its canonical points at https://stackshare.io/ —
    // the homepage, not this page. StackShare's bug, not ours. A page that
    // canonicalises away from itself tells Google to index something else,
    // so treat this as effectively non-indexable until they fix it.
    indexable: false,
    verified:
      '2026-08-24: public page renders the SendVC description and the no-commission / no-equity framing, listed under Business Tools > Marketing Automation.',
  },
  {
    href: 'https://www.f6s.com/software/sendvc',
    label: 'F6S',
    indexable: false,
    verified:
      '2026-08-26: rendered in a signed-in browser — shows our real pricing tiers and names Future Webservice. Served noindex. NOTE the path: f6s.com/sendvc is a hard 404, and f6s.com/software/review?product=sendvc is only a review FORM that renders for any product parameter.',
  },
  {
    href: 'https://smollaunch.com/products/sendvc',
    label: 'SmolLaunch',
    indexable: false,
    verified:
      '2026-10-15: NOW PUBLISHED. h1 "SendVC", self-referencing canonical, contains sendvc.app; control slug /products/zzq-not-real-9931 returns 404. Served noindex,nofollow. On 2026-08-30 this exact URL was a SOFT-404 that redirected to /products with zero "sendvc" hits — the draft has since gone live, so a past negative is not permanent. Re-check rejected URLs periodically.',
  },
  {
    href: 'https://earlyhunt.com/project/sendvc',
    label: 'EarlyHunt',
    indexable: false,
    verified: '2026-08-26: live launch-directory listing, ours.',
  },
  {
    href: 'https://futurewebservice.de/',
    label: 'Future Webservice',
    indexable: true,
    verified:
      '2026-08-21: the operator\'s own corporate site. Carries the SAME legal entity and registered address this codebase and /imprint assert, and its portfolio names SendVC as one of its products. The strongest corroboration available that is not a directory listing.',
  },
];

/**
 * CHECKED AND REJECTED — kept so they stop being re-proposed from stale
 * checklists. Each was reproduced against its own control slug.
 *
 *   pitchwall.co/product/sendvc          hard 404
 *   alternativeto.net/software/sendvc    hard 404
 *   f6s.com/sendvc                       hard 404 (the profile is /software/sendvc)
 *   g2.com/products/sendvc/reviews       live and ours, but served follow,noindex.
 *                                        Not a G2-wide default: /products/slack/reviews
 *                                        serves follow,index. Add once it flips.
 *   startupbase.io/products/sendvc       approved but UNPUBLISHED. Its response body is
 *                                        byte-identical to an invented slug, while
 *                                        /products/startupbase returns 200 with real content.
 *   saashub.com/sendvc-alternatives      REMOVED BY SAASHUB 2026-10-15. Was live and in
 *                                        sameAs since August; now a hard 404 whose body is
 *                                        byte-identical (3,692) for the old URL, /sendvc,
 *                                        and an invented slug. Deleted from sameAs — this
 *                                        is exactly the Product Hunt failure this file
 *                                        exists to prevent, and it is why the list needs
 *                                        re-checking on a cadence, not just on add.
 *   f6s.com/software/review?product=…    a review FORM, not a profile. Renders for ANY
 *                                        product parameter, so reaching it proves nothing.
 *   news.ycombinator.com/item?id=49435986  DEAD. HN's own Firebase API returns dead:true on
 *                                        both story and comment. HN serves dead items as
 *                                        HTTP 200 with a near-blank body, so a status check
 *                                        calls it healthy.
 *   tinylaunch.com/startup/sendvc      307 -> /login. Not public. (/launch/sendvc is a
 *                                        plain 404.) Its free slot is dated Sep 28 2026.
 *   saasworthy.com/product/sendvc        HTTP 500, body BYTE-IDENTICAL (72,610) to an
 *                                        invented slug, while /product/slack returns 200
 *                                        with 242KB. Submitted 2026-08-24, never published.
 *   TrustRadius / LaunchingNext /
 *   StartupBuffer                        pending or unpublished.
 */

/**
 * Every confirmed URL, for the Organization JSON-LD `sameAs`.
 *
 * Includes the noindex entries deliberately. `noindex` means "do not show this
 * in search results" — it does not mean "do not crawl", and Google can still
 * use a crawled page for entity association. They contribute less than the
 * indexable ones, not nothing, and the cost of listing them is zero.
 */
export const PROFILE_SAME_AS: string[] = SENDVC_PROFILES.map((p) => p.href);

/**
 * The subset shown as visible links in the footer.
 *
 * Kept short on purpose. An outbound link from our footer passes authority TO
 * the directory, not to us — the SEO value of these profiles is the inbound
 * link and the `sameAs` identity assertion, neither of which depends on us
 * linking back. So the visible row is justified only by human recognition, and
 * it lists the names a founder would actually recognise. The rest still ship
 * in `sameAs` above, where they do their real work.
 */
/**
 * THERE IS DELIBERATELY NO VISIBLE LIST OF THESE IN THE UI.
 *
 * A footer row linking them was built and then removed on 2026-08-26, for
 * three reasons worth recording so it does not get rebuilt on reflex:
 *
 *  1. An outbound link passes authority TO the directory, not to us. The SEO
 *     value of one of these profiles is the INBOUND link and the `sameAs`
 *     identity assertion — neither depends on us linking back.
 *  2. Referral traffic from a muted row at the bottom of a footer is
 *     effectively zero.
 *  3. Decisively: every one of these profiles currently has ZERO reviews. A
 *     "Trustpilot" link that lands a prospective customer on 0.0/5 and "This
 *     company hasn't received any reviews yet" is worse than no link at all.
 *     These become a trust signal when they carry real ratings, not before.
 *
 * This is NOT cloaking. `sameAs` is structured data — a machine-facing
 * identity assertion by design, which is the documented mechanism for exactly
 * this. Cloaking means showing crawlers VISIBLE CONTENT that humans cannot
 * see; declaring an entity's other profiles in JSON-LD is not that. (Hiding
 * these as `display:none` anchors WOULD be, which is why that was refused.)
 *
 * If a trust row is ever wanted, it belongs high on the page with real
 * ratings on it, not as footer filler — and it needs the reviews first.
 */
